"use client";
import Link from "next/link";
import React, { useState, useCallback, memo } from "react";
import { FaPlus, FaMinus } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

// FAQ Data
const faqs = [
  {
    question: "What is Bright DiGi Gold?",
    answer:
      "Bright DiGi Gold is a user-friendly platform where you can grow your savings in pure digital gold and silver.",
  },
  {
    question: "What is digital gold?",
    answer:
      "Digital Gold is real gold. It can easily be converted into physical gold with a single click, while being stored digitally to save space and ensure security.",
  },
  {
    question: "Is it safe to use the Bright DiGi Gold?",
    answer:
      "Yes, the Bright DiGi Gold platform is 100% safe and secure for your digital gold and silver savings. It is powered by Bright Gold (Bright Metal Refiners) as our Gold Partner, with payments processed over a secure banking network.",
  },
];

// Animation variants for the answer section
const answerVariants = {
  open: {
    height: "auto",
    opacity: 1,
    transition: { duration: 0.2, ease: "easeInOut" },
  },
  closed: {
    height: 0,
    opacity: 0,
    transition: { duration: 0.3, ease: "easeInOut" },
  },
};

// **Memoized FAQ Item for Performance**
const FaqItem = memo(({ faq, index, openIndex, toggleIndex }: any) => {
  const isOpen = openIndex === index;

  return (
    <div className="pt-4">
      {/* Question Section */}
      <button
        className={`faq-back flex w-full items-start justify-between text-left text-white px-4 py-4 transition-all duration-500 ease-in-out ${isOpen ? "rounded-t-2xl" : "rounded-2xl"}`}
        onClick={() => toggleIndex(index)}
        aria-expanded={isOpen}
      >
        <span className="text-base poppins-medium leading-7">{faq.question}</span>
        <span className="ml-6 flex h-7 items-center">
          {isOpen ? (
            <FaMinus className="h-6 w-6" aria-hidden="true" />
          ) : (
            <FaPlus className="h-6 w-6" aria-hidden="true" />
          )}
        </span>
      </button>

      {/* Answer Section with Animation */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            variants={answerVariants}
            initial="closed"
            animate="open"
            exit={{ opacity: 1 }}
            layout
            className="text-base poppins-regular leading-7 text-gray-600 rounded-b-2xl px-4 py-2 bg-themeBlue overflow-hidden"
          >
            {faq.answer}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

FaqItem.displayName = "FaqItem"; // Fix for React.memo

// **Main FAQ Component**
const Faq = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // **UseCallback to prevent unnecessary re-renders**
  const toggleIndex = useCallback(
    (index: number) => setOpenIndex(openIndex === index ? null : index),
    [openIndex]
  );

  return (
    <div className="bg-theme relative">
      <div className="mx-auto px-4 sm:px-6 lg:px-16 py-12 pb-20">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-yellow-500 text-3xl sm:text-5xl font-extrabold text-center mx-auto">
            FAQ's
          </h1>
          <Link
            href="/faqs"
            className="bg-themeLight px-3 py-1 text-md text-white rounded border border-gray-500"
          >
            View All
          </Link>
        </div>

        {/* FAQ List */}
        <dl className="mt-8 space-y-1 divide-y divide-gray-800">
          {faqs.map((faq, index) => (
            <FaqItem
              key={index}
              faq={faq}
              index={index}
              openIndex={openIndex}
              toggleIndex={toggleIndex}
            />
          ))}
        </dl>
      </div>
    </div>
  );
};

export default Faq;